/**
 * BTC long-term price model (Patch BS rewrite).
 *
 * Pure power law formulation:
 *
 *   log10(P) = exponent * log10(day) + logIntercept
 *
 *   equivalently  P = 10^logIntercept * day^exponent
 *
 *   where day = days elapsed from anchorDate (Bitcoin genesis, Jan 3, 2009).
 *
 * Pre-BS used Santostasi's "stretching doublings" derivation anchored at
 * Pizza Day. That form converges to the same asymptotic power law but has
 * a curved tail near its anchor that's mathematically real but visually
 * confusing. The pure form is straight on log-log axes from day 1 onwards
 * and matches every published Bitcoin Power Law chart.
 *
 * Defaults (WORKBOOK_PRICE_MODEL):
 *   exponent     = 5.69    (published OLS slope; R^2 ~0.96 over 2010-2024)
 *   logIntercept = -16.52  (calibrates the line through historical median)
 *   anchorDate   = 2009-01-03 (Bitcoin genesis block)
 *
 * The function signatures (projectBtcPrice, priceFromDay, daysBetween,
 * generateBtcPriceSeries) are unchanged from pre-BS so downstream callers
 * (projection-modes.ts, carry.ts, loop-loan.ts, etc.) pick up the new
 * formula transparently.
 */

import { Decimal } from './money.js';
import type { BtcPriceModel } from './types.js';
import type { Usd } from './money.js';

const MS_PER_DAY = 86_400_000;

/** Pre-computed ln(10) used to convert natural logs to log10s. Decimal.js
 *  has Decimal.log(x, base) in some versions but the lookup-by-base API
 *  varies; using ln/LN10 works everywhere. */
const LN10 = new Decimal(10).ln();

/** Days between two dates as a Decimal (preserves fractional days from time-of-day). */
export function daysBetween(start: Date, end: Date): Decimal {
  const ms = end.getTime() - start.getTime();
  return new Decimal(ms).div(MS_PER_DAY);
}

/**
 * Project BTC price at a given date using the model. Manual overrides take
 * precedence on exact-date match.
 */
export function projectBtcPrice(date: Date, model: BtcPriceModel): Usd {
  if (model.manualOverrides) {
    const override = model.manualOverrides.find(o => sameInstant(o.date, date));
    if (override) return override.priceUsd;
  }
  const day = daysBetween(model.anchorDate, date);
  return priceFromDay(day, model);
}

/** Project a price from `day` (days since model.anchorDate). Pure power law:
 *  P = 10^logIntercept * day^exponent. Returns 0 at or before the anchor
 *  (log of zero/negative is undefined; the model isn't meaningful there). */
export function priceFromDay(day: Decimal, model: BtcPriceModel): Usd {
  if (day.lessThanOrEqualTo(0)) {
    // Pre-anchor or genesis itself. Pure power law has no defined value;
    // return 0 so callers can detect with .isZero() and substitute.
    return new Decimal(0);
  }
  // log10(P) = exponent * log10(day) + logIntercept
  const log10Day = day.ln().div(LN10);
  const log10P = model.logIntercept.plus(model.exponent.times(log10Day));
  // P = 10^log10P
  return new Decimal(10).pow(log10P);
}

/**
 * Generate a price series at the model's stepDays cadence between [start, end].
 * Same shape as pre-BS: inclusive of start, walks forward by stepDays.
 */
export function generateBtcPriceSeries(
  model: BtcPriceModel,
  start: Date,
  end: Date,
): Array<{ date: Date; day: Decimal; price: Usd }> {
  const stepMs = model.stepDays.times(MS_PER_DAY).toNumber();
  const out: Array<{ date: Date; day: Decimal; price: Usd }> = [];
  let cursor = start.getTime();
  const endMs = end.getTime();
  while (cursor <= endMs) {
    const date = new Date(cursor);
    const day = daysBetween(model.anchorDate, date);
    out.push({ date, day, price: priceFromDay(day, model) });
    cursor += stepMs;
  }
  return out;
}

function sameInstant(a: Date, b: Date): boolean {
  return a.getTime() === b.getTime();
}

/**
 * Default model: published pure-power-law fit.
 *
 * Source: the standard Bitcoin Power Law parameters cited across community
 * charts (apsk32 and others). Anchored at the genesis block. R^2 ~0.96
 * over the full historical period. With these defaults:
 *   - Pizza Day (day 504):  ~$0.071  (actual: $0.062, within fit quality)
 *   - 2026-01:              ~$110k   (actual: ~$90-110k)
 *   - 2030-01:              ~$385k
 *   - 2040-01:              ~$3.5M
 *   - 2050-01:              ~$17M
 */
export const WORKBOOK_PRICE_MODEL: BtcPriceModel = {
  id: 'power-law-default',
  name: 'Bitcoin Power Law (Genesis-anchored)',
  anchorDate: new Date('2009-01-03T00:00:00Z'),
  exponent: new Decimal('5.69'),
  logIntercept: new Decimal('-16.52'),
  stepDays: new Decimal('30.44'),
};
